import type { AgentTokenExpiryDays } from 'definitions/agent-tokens';
import type { AgentTokenWebsiteOption } from '@/state/useAgentTokens';
import type { WebsiteMode } from '../Dashboard/Settings/AgentAccess/use-agent-access';
import type { ConnectManager } from './use-connect';
import { Link, useParams } from 'react-router';
import { Info } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/Select';
import { Panel, Field, FIELD_CONTROL } from '../Dashboard/ConsolePanel';
import { LoadFailed } from '../Dashboard/Settings/AgentAccess/LoadFailed';
import { WebsiteRow, WebsiteSelectAll } from '../Dashboard/Settings/AgentAccess/WebsiteSelectAll';
import { AGENT_ACCESS_COPY, SCOPE_COPY } from '../Dashboard/Settings/AgentAccess/agent-access-copy';
import { CONNECT_COPY } from './connect-copy';
import { isFramed } from './is-framed';
import { useConnect } from './use-connect';

const COPY = CONNECT_COPY;

type ManagerProps = { manager: ConnectManager };

/**
 * The OAuth consent screen (PER-334), at /connect/<uid>, where the authorization server sends the
 * browser when an app such as claude.ai or ChatGPT asks to connect. The person is already signed in
 * to the dashboard (the router sends them through sign-in first). They see who is asking, choose
 * what it may do, on which websites and for how long, confirm with their password (and passkey,
 * if they have one), and are sent back to the app.
 *
 * The app's host leads, because it is the one thing about the app the server has proved: the name
 * and logo are its own claims, and the logo is shown only when served from that same host. Apps on
 * the server's verified list (Claude, Claude Code, ChatGPT) are marked so; any other is marked as an
 * unverified app, with a warning, and staff accounts cannot connect one at all.
 */
export function ConnectScreen() {
  const { uid = '' } = useParams();
  const manager = useConnect(uid);
  const { details, isLoading, loadError } = manager;

  // Never answered inside a frame: another page could otherwise dress it up and click it for the
  // person (the server cannot send frame-ancestors for the SPA's own pages).
  if (isFramed()) {
    return (
      <main className="bg-background min-h-screen px-4 py-10">
        <p className="supporting mx-auto m-0 max-w-xl">{COPY.framed}</p>
      </main>
    );
  }

  return (
    <main className="bg-background min-h-screen px-4 py-10">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-[14px]">
        {loadError ? (
          <Panel title={COPY.pageTitle}>
            <div className="flex flex-col items-start gap-3">
              <p className="supporting text-danger m-0">{loadError}</p>
              <Button variant="ghost" size="sm" onClick={() => window.location.assign('/')} className="h-8 rounded-lg">
                {COPY.backHome}
              </Button>
            </div>
          </Panel>
        ) : isLoading || !details ? null : (
          <ConsentPanel manager={manager} />
        )}
      </div>
    </main>
  );
}

const ConsentPanel = ({ manager }: ManagerProps) => {
  const client = manager.details!.client;
  const busy = manager.pending !== null;
  const nothingToAllow = manager.allowedScopes.length === 0;
  const refusal = manager.details!.refusal;
  const cannotAllow = nothingToAllow || !!refusal || !!manager.optionsError;

  return (
    <Panel
      title={COPY.pageTitle}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!!manager.actionError && (
            <span role="alert" className="supporting text-danger">
              {manager.actionError}
            </span>
          )}
          {!!manager.blockingReason && !cannotAllow && <span className="supporting">{manager.blockingReason}</span>}
          <Button variant="ghost" onClick={manager.deny} disabled={busy} className="h-9 rounded-lg">
            {COPY.deny}
          </Button>
          <Button
            onClick={manager.allow}
            disabled={!!manager.blockingReason || busy || cannotAllow}
            title={manager.blockingReason}
            className="h-9 rounded-lg"
          >
            {manager.pending === 'allow' ? COPY.allowing : manager.pending === 'leaving' ? COPY.leaving : COPY.allow}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!!manager.passkeyNotice && <PasskeyNotice text={manager.passkeyNotice} />}
        <div className="flex items-start gap-3">
          {client.logoUri && (
            <img
              src={client.logoUri}
              alt=""
              width={40}
              height={40}
              referrerPolicy="no-referrer"
              className="border-line rounded-lg border object-contain"
            />
          )}
          <div className="grid min-w-0 gap-1">
            <h1 className="text-foreground m-0 text-body font-medium break-words">{COPY.heading(client.host)}</h1>
            {client.name && <p className="supporting m-0 break-words">{COPY.calledItself(client.name)}</p>}
            <p className={cn('m-0 text-meta', client.verified ? 'text-ok' : 'text-danger')}>
              {client.verified ? COPY.verified : COPY.unverified}
            </p>
          </div>
        </div>
        {client.verified ? (
          <p className="supporting m-0">{COPY.verifiedHelp}</p>
        ) : (
          <p className="border-line bg-panel2 supporting m-0 rounded-lg border p-3">{COPY.unverifiedHelp}</p>
        )}
        <p className="supporting m-0">{COPY.intro}</p>
        {client.loopback ? (
          <p className="border-line bg-panel2 supporting m-0 rounded-lg border p-3">{COPY.loopback}</p>
        ) : (
          <p className="supporting m-0">{COPY.returnTo(client.redirectHost)}</p>
        )}
        {refusal ? (
          <p className="supporting text-danger m-0">{refusal}</p>
        ) : nothingToAllow ? (
          <p className="supporting text-danger m-0">{COPY.nothingToAllow}</p>
        ) : manager.optionsError ? (
          <LoadFailed text={manager.optionsError} onRetry={manager.retryOptions} />
        ) : (
          <>
            <ScopesField manager={manager} />
            <WebsitesField manager={manager} />
            <ExpiryField manager={manager} />
            <PasswordField manager={manager} />
          </>
        )}
      </div>
    </Panel>
  );
};

type PasskeyNoticeProps = { text: string };

/**
 * Shown when the account's passkey is registered on another host: passkeys are bound to the host they
 * were registered on, so this page cannot ask for it and Allow stays off. As on the token form.
 */
const PasskeyNotice = ({ text }: PasskeyNoticeProps) => (
  <div role="status" className="border-line bg-panel2 rounded-card flex items-start gap-2.5 border p-3.5">
    <Info className="text-dim mt-0.5 h-4 w-4 shrink-0" aria-hidden />
    <div className="grid gap-1">
      <p className="text-foreground m-0 text-body">{text}</p>
      <Link to="/settings/account" className="supporting text-foreground underline underline-offset-2">
        {COPY.managePasskeys}
      </Link>
    </div>
  </div>
);

const ScopesField = ({ manager }: ManagerProps) => {
  const requested = manager.details!.requestedScopes;
  return (
    <fieldset className="m-0 grid gap-2 border-0 p-0">
      <legend className="micro-label mb-1.5">{COPY.scopesLabel}</legend>
      {requested.map((scope) => {
        const allowed = manager.allowedScopes.includes(scope);
        return (
          <label key={scope} className={cn('flex items-start gap-2.5', allowed ? 'cursor-pointer' : 'opacity-60')}>
            <Checkbox
              checked={allowed && manager.scopes.includes(scope)}
              disabled={!allowed}
              onCheckedChange={() => manager.toggleScope(scope)}
              className="mt-0.5"
              aria-describedby={`connect-scope-${scope}`}
            />
            <span className="grid gap-0.5">
              <span className="text-foreground text-body">{SCOPE_COPY[scope].label}</span>
              <span id={`connect-scope-${scope}`} className="supporting">
                {allowed ? SCOPE_COPY[scope].description : COPY.notForYourRole}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
};

const MODES: readonly { value: WebsiteMode; label: string }[] = [
  { value: 'all', label: AGENT_ACCESS_COPY.allWebsites },
  { value: 'some', label: AGENT_ACCESS_COPY.someWebsites },
];

const groupByCompany = (websites: readonly AgentTokenWebsiteOption[]) => {
  // Built up in place: the list can run to thousands, and this runs on every keystroke in the form.
  const groups = new Map<number, { name: string; websites: AgentTokenWebsiteOption[] }>();
  for (const website of websites) {
    const group = groups.get(website.companyId);
    if (group) group.websites.push(website);
    else groups.set(website.companyId, { name: website.companyName, websites: [website] });
  }
  return [...groups.entries()];
};

const WebsitesField = ({ manager }: ManagerProps) => {
  const websites = manager.options?.websites ?? [];
  const groups = groupByCompany(websites);
  const selected = new Set(manager.websiteIds);
  return (
    <fieldset className="m-0 grid gap-2 border-0 p-0">
      <legend className="micro-label mb-1.5">{AGENT_ACCESS_COPY.websitesLabel}</legend>
      {/* A staff connection that can make changes must name its sites, as a staff token must. */}
      {manager.staffWrite ? (
        <p className="supporting m-0">{AGENT_ACCESS_COPY.staffAllWebsites}</p>
      ) : (
        <div role="radiogroup" className="flex flex-wrap gap-4">
          {MODES.map((mode) => (
            <label key={mode.value} className="text-foreground flex cursor-pointer items-center gap-2 text-body">
              <input
                type="radio"
                name="connect-websites"
                value={mode.value}
                checked={manager.websiteMode === mode.value}
                onChange={() => manager.setWebsiteMode(mode.value)}
                className="accent-primary"
              />
              {mode.label}
            </label>
          ))}
        </div>
      )}
      {manager.websiteMode === 'some' && (
        <div className="border-line bg-panel2 rounded-card border">
          <WebsiteSelectAll
            selected={manager.websiteIds.length}
            total={websites.length}
            onToggle={manager.toggleAllWebsites}
          />
          <div className="grid max-h-72 gap-3 overflow-y-auto p-3">
            {groups.map(([companyId, group]) => (
              <div key={companyId} className="grid gap-1.5">
                {groups.length > 1 && <span className="micro-label">{group.name}</span>}
                {group.websites.map((website) => (
                  <WebsiteRow
                    key={website.id}
                    url={website.url}
                    checked={selected.has(website.id)}
                    onToggle={() => manager.toggleWebsite(website.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </fieldset>
  );
};

const ExpiryField = ({ manager }: ManagerProps) => (
  <Field label={COPY.expiryLabel} htmlFor="connect-expiry">
    <Select
      name="connect-expiry"
      value={String(manager.expiresInDays)}
      options={(manager.options?.expiryDays ?? []).map((days) => ({
        value: days,
        label: AGENT_ACCESS_COPY.expiryOption(days),
      }))}
      onValueChange={(value: string) => manager.setExpiresInDays(Number(value) as AgentTokenExpiryDays)}
      className={cn(FIELD_CONTROL, 'max-w-xs')}
    />
  </Field>
);

const PasswordField = ({ manager }: ManagerProps) => (
  <Field
    label={AGENT_ACCESS_COPY.passwordLabel}
    htmlFor="connect-password"
    hint={manager.options?.passkeyStepUp ? `${COPY.passwordHelp} ${COPY.passkeyNote}` : COPY.passwordHelp}
  >
    <Input
      id="connect-password"
      type="password"
      autoComplete="current-password"
      value={manager.password}
      onChange={(ev) => manager.setPassword(ev.target.value)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          void manager.allow();
        }
      }}
      className={cn(FIELD_CONTROL, 'max-w-xs')}
    />
  </Field>
);
